import * as pdfjs from "pdfjs-dist";

let workerInitialized = false;

export const initializePdfWorker = (): void => {
  if (workerInitialized) {
    return;
  }

  if (typeof Worker !== "undefined") {
    pdfjs.GlobalWorkerOptions.workerPort = new Worker(
      new URL("pdfjs-dist/build/pdf.worker.mjs", import.meta.url),
      { type: "module" },
    );
  }

  workerInitialized = true;
};

export const getPdfjs = (): typeof pdfjs => {
  initializePdfWorker();
  return pdfjs;
};

const PDF_MIME_TYPE = "application/pdf";
const PNG_MIME_TYPE = "image/png";

const getBaseFileName = (fileName: string): string => {
  const dotIndex = fileName.lastIndexOf(".");
  return dotIndex > 0 ? fileName.substring(0, dotIndex) : fileName;
};

export const convertPdfPagesToImages = async (file: File): Promise<File[]> => {
  const pdfjsInstance = getPdfjs();
  const fileUrl = URL.createObjectURL(file);

  try {
    const pdf = await pdfjsInstance.getDocument(fileUrl).promise;
    const pdfPages = await Promise.all(
      Array.from({ length: pdf.numPages }).map((_, pageIndex) =>
        pdf.getPage(pageIndex + 1),
      ),
    );
    const pdfFileName = getBaseFileName(file.name);

    return await Promise.all(
      pdfPages.map(async (page) => {
        const viewport = page.getViewport({ scale: 1.5 });
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");

        if (!context) {
          throw new Error("Failed to create canvas context");
        }

        canvas.width = viewport.width;
        canvas.height = viewport.height;

        await page.render({ canvasContext: context, viewport }).promise;

        const pageImageBlob = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob((blob) => {
            if (!blob) {
              reject(new Error("Failed to create blob from canvas"));
            } else {
              resolve(blob);
            }
          }, PNG_MIME_TYPE);
        });

        const pdfPageImageFileName = `${pdfFileName}-page-${page.pageNumber}.png`;
        const pdfPageImageFile = new File(
          [pageImageBlob],
          pdfPageImageFileName,
          {
            type: PNG_MIME_TYPE,
          },
        );

        return pdfPageImageFile;
      }),
    );
  } finally {
    URL.revokeObjectURL(fileUrl);
  }
};

export const getFileUrlsForFile = async (file: File): Promise<string[]> => {
  if (file.type !== PDF_MIME_TYPE) {
    return [URL.createObjectURL(file)];
  }

  const imageFilesForPdfPages = await convertPdfPagesToImages(file);

  return imageFilesForPdfPages.map(URL.createObjectURL);
};

export { pdfjs };
